import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";

type Submission = {
  id: number;
  title: string;
  note: string | null;
  files: { url: string; name: string; size?: number; type?: string }[];
  status: string;
  reject_reason: string | null;
  created_at: string;
};

type UserDetail = {
  id: number;
  email: string;
  display_name: string;
  company: string | null;
  phone: string | null;
  active: boolean;
  status: "pending" | "approved" | "rejected";
  user_type: "normal" | "lp";
  qualified_status: string;
  created_at: string;
  submissions: Submission[];
};

const STATUS_LABEL: Record<string, string> = { pending: "待审核", approved: "已通过", rejected: "已拒绝" };

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<UserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 可编辑表单（所有字段统一一个保存按钮提交）
  const [form, setForm] = useState({ display_name: "", email: "", company: "", phone: "", new_password: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(null);
    api.get<UserDetail>(`/api/admin/studio/users/${id}`)
      .then((d) => { setData(d); setForm({ display_name: d.display_name, email: d.email, company: d.company || "", phone: d.phone || "", new_password: "" }); })
      .catch((e) => setError(String(e)));
  };
  useEffect(load, [id]);

  const save = async () => {
    if (!data) return;
    if (form.new_password && form.new_password.length < 6) { setMsg("新密码至少 6 位"); return; }
    const body: Record<string, unknown> = {
      display_name: form.display_name,
      email: form.email,
      company: form.company || null,
      phone: form.phone || null,
    };
    if (form.new_password) body.new_password = form.new_password;
    setSaving(true); setMsg(null);
    try {
      const updated = await api.patch<UserDetail>(`/api/admin/studio/users/${data.id}`, body);
      setData((prev) => (prev ? { ...prev, ...updated } : prev));
      setForm((f) => ({ ...f, new_password: "" }));
      setMsg("已保存");
    } catch (e) {
      const err = e instanceof ApiError && (e.body as any)?.error === "email_taken" ? "该邮箱已被使用"
        : e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e);
      setMsg(err);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="muted">加载中…</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">用户明细 · {data.display_name}</div>
        <div className="page-actions">
          <button className="btn" onClick={() => nav("/studio/users")}>← 返回列表</button>
        </div>
      </div>

      {msg && <div className="muted" style={{ marginBottom: 12 }}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>
        {/* 资料编辑 —— 所有字段统一一个保存按钮 */}
        <div className="form profile-form">
          <div className="ce-label" style={{ marginBottom: 12 }}>账户资料</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
            注册于 {new Date(data.created_at).toLocaleDateString("zh-CN")}
            {" · "}{data.user_type === "lp" ? "LP 投资人" : "普通用户"}
            {" · "}状态：{data.active ? STATUS_LABEL[data.status] : "已停用"}
          </div>

          <label className="ce-label">姓名</label>
          <input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
          <label className="ce-label" style={{ marginTop: 10 }}>邮箱（登录账号）</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <label className="ce-label" style={{ marginTop: 10 }}>机构 / 公司</label>
          <input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
          <label className="ce-label" style={{ marginTop: 10 }}>电话</label>
          <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          <label className="ce-label" style={{ marginTop: 10 }}>重置密码（留空则不改，至少 6 位）</label>
          <input type="text" value={form.new_password} placeholder="新密码" onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} />

          <button className="btn btn-primary" style={{ marginTop: 16 }} disabled={saving} onClick={save}>
            {saving ? "保存中…" : "保存"}
          </button>
        </div>

        {/* 提交记录（可点击进明细 = 跳智库提交并展开该条） */}
        <div className="form">
          <div className="ce-label" style={{ marginBottom: 12 }}>提交记录 · {data.submissions.length}</div>
          {data.submissions.length === 0 && <div className="muted" style={{ fontSize: 13 }}>暂无提交</div>}
          {data.submissions.map((s) => (
            <div key={s.id} onClick={() => nav(`/studio/submissions/${s.id}`)}
              style={{ padding: "12px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontSize: 13 }}>{s.title || "未命名"} · {s.files.length} 份资料</span>
                <span className="muted" style={{ fontSize: 11 }}>{new Date(s.created_at).toLocaleDateString("zh-CN")}</span>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                状态：{s.status === "done" ? "已交付" : s.status === "processing" ? "制作中" : s.status === "rejected" ? "被驳回" : "待处理"}
                <span className="slug" style={{ marginLeft: 10 }}>进入明细 →</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
